import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import * as React from "react";
import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useGetLoggedInUserDetails } from "../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { ReactComponent as ClientContactIcon } from "../../../../assets/images/client-contact-icon.svg";

import { useSelector } from "react-redux";
import {
  ChipOptionsDataType,
  FormController,
  MuiBox,
  MuiGrid,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
} from "../../../../components";
import MuiChipWithPopover from "../../../../components/chipWithPopover/MuiChipWithPopover";
import MuiCustomMenu from "../../../../components/customMenu/MuiCustomMenu";
import { MuiDateField } from "../../../../components/dateField/MuiDateField";
import MuiEllipseLabel from "../../../../components/ellipseLabel/MuiEllipseLabel";
import { MuiLink } from "../../../../components/link/MuiLink";
import { StatusBadge } from "../../../../components/statusBadge/StatusBadge";
import { PAGE_IDS } from "../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../helpers/constants/countryDateFormat";
import { MAX_LENGTH_50 } from "../../../../helpers/constants/validations";
import {
  convertToUserFormat,
  getContactTypeNamesString,
  getFullName,
  getUsedByClientNamesString,
  getValueOrBlank,
  getYesNoString,
  isEmpty,
  isNotEmpty,
  isRetailClient,
} from "../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { createClientSelector } from "../../../../redux/slices/client/createClientSelector";
import { isContactBelongCurrentClient } from "../../helper";
import {
  ClientContact,
  ClientContactResultFormKey,
} from "../../modelsAndSchemas/Models";
import { createClientSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { ClientContactResultWrapper } from "./ClientContactResult.style";
import { OptionMenu } from "./optionMenu/OptionMenu";

export const pageId = PAGE_IDS.CREATE_CLIENT_CONTACT_RESULT;
const BE_ERROR_CODE_CONTACT_USED_IN_BILLING = "4068";
export interface ClientResultFormProps {
  rows: ClientContact[];
  onClientEdit: (row: ClientContact) => void;
  onClientView: (row: ClientContact) => void;
  onDelete: (row: ClientContact) => void;
  isEditable?: boolean;
}

export const ClientContactResult: FC<ClientResultFormProps> = ({
  rows,
  onClientEdit,
  onClientView,
  onDelete,
  isEditable = true,
}) => {
  const {
    fetchLabel,
    commonLabels,
    fetchDropdownValues,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const tableRef = React.useRef<{ resetPagination: () => void }>(null);
  const [filteredParentContactResults, setFilteredParentContactResults] =
    useState<ClientContact[]>([]);
  const [
    filteredCurrentClientContactResults,
    setFilteredCurrentClientContactResults,
  ] = useState<ClientContact[]>([]);

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const { control, watch, setValue, getValues } = useForm({
    mode: "all",
    defaultValues: {
      [ClientContactResultFormKey.contactName]: "",
      [ClientContactResultFormKey.usedByClients]: "",
      [ClientContactResultFormKey.usedByClientsName]: "",
      [ClientContactResultFormKey.contactType]: "",
      [ClientContactResultFormKey.isSurveyContact]: "",
      [ClientContactResultFormKey.status]: "",
      [ClientContactResultFormKey.contactNumber]: "",
      [ClientContactResultFormKey.lastModifiedOn]: "",
    },
  });

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  const deleteContactAction = (row: ClientContact) => {
    onDelete(row);
  };

  const getLabelFromContacts = (key: string) => {
    return fetchLabel(createClientSiteCoreKeys.contactsTitle, key);
  };

  const filterData = (
    rows: ClientContact[],
    contactName: string,
    usedByClientsName: string,
    contactType: string,
    isSurveyContact: string,
    status: string,
    contactNumber: string,
    lastModifiedOn: string,
  ) => {
    return rows.filter((item) => {
      item.contactName = getFullName(item.firstName, item.lastName, true);
      item.isSurveyContact = item.isSurveyContact || false;
      item.usedByClientsName = getUsedByClientNamesString(item.usedByClients);
      return (
        item.contactName.toLowerCase().includes(contactName.toLowerCase()) &&
        item.usedByClientsName
          ?.toLowerCase()
          .includes(usedByClientsName.toLowerCase()) &&
        (isEmpty(contactType) ||
          getContactTypeNamesString(item.contactType as unknown as string[])
            ?.toLowerCase()
            .includes(contactType.toLowerCase())) &&
        (isEmpty(isSurveyContact) ||
          getYesNoString(item.isSurveyContact || false)
            .toLowerCase()
            .includes(isSurveyContact.toLowerCase())) &&
        (isEmpty(status) ||
          fetchValueByKeyOfDropdown(
            createClientSiteCoreKeys.contactsTitle,
            createClientSiteCoreKeys.contactStatusList,
            item.status,
          ).toLowerCase() === status.toLowerCase()) &&
        getValueOrBlank(item.contactNumber)
          .toLowerCase()
          .includes(getValueOrBlank(contactNumber).toLowerCase()) &&
        convertToUserFormat(
          item.lastModifiedOn,
          userCountryDateFormat,
        ).includes(lastModifiedOn)
      );
    });
  };

  useEffect(() => {
    const {
      contactName,
      usedByClientsName,
      contactType,
      isSurveyContact,
      status,
      contactNumber,
      lastModifiedOn,
    } = getValues();

    const filteredParentContactResultsTemp = rows.filter(
      (contact) => !contact.isAttachedToCurrentClient,
    );

    const filteredCurrentClientContactResultsTemp = rows.filter(
      (contact) => contact.isAttachedToCurrentClient,
    );

    const parentContactResults = filterData(
      filteredParentContactResultsTemp,
      contactName,
      usedByClientsName,
      contactType,
      isSurveyContact,
      status,
      contactNumber,
      lastModifiedOn,
    );
    const currentClientContactResults = filterData(
      filteredCurrentClientContactResultsTemp,
      contactName,
      usedByClientsName,
      contactType,
      isSurveyContact,
      status,
      contactNumber,
      lastModifiedOn,
    );

    resetPagination();
    setFilteredParentContactResults([...parentContactResults]);
    setFilteredCurrentClientContactResults([...currentClientContactResults]);
  }, [
    rows,
    watch(ClientContactResultFormKey.contactName),
    watch(ClientContactResultFormKey.usedByClientsName),
    watch(ClientContactResultFormKey.contactType),
    watch(ClientContactResultFormKey.isSurveyContact),
    watch(ClientContactResultFormKey.status),
    watch(ClientContactResultFormKey.contactNumber),
    watch(ClientContactResultFormKey.lastModifiedOn),
  ]);

  const handleMuiDateChange = (newValue: dayjs.Dayjs | null) => {
    let dateValue = "";
    if (newValue?.isValid()) {
      dateValue = newValue.format(userCountryDateFormat);
    }
    setValue(ClientContactResultFormKey.lastModifiedOn, dateValue);
  };

  const columns = [
    {
      id: ClientContactResultFormKey.contactName,
      label: getLabelFromContacts(createClientSiteCoreKeys.contactName),
      render: (row: any) => {
        const fullName = getFullName(row.firstName, row.lastName, true);
        if (!fullName) {
          return null;
        }
        return (
          <MuiLink
            aria-label={`${fullName}`}
            onClick={() => onClientView?.(row as unknown as ClientContact)}
          >
            <MuiBox className="contact-name-wrapper">
              <MuiEllipseLabel
                aria-hidden
                labelTypoClassName="Link contact-name-link"
                text={fullName}
                labelTypoVariant="h3"
                placement="bottom-start"
                pointerCursor
              />
              {isContactBelongCurrentClient(row) && (
                <MuiBox className="client-contact-icon">
                  <ClientContactIcon
                    aria-hidden
                    data-testid={`client-contact-icon-${row}`}
                  />
                </MuiBox>
              )}
            </MuiBox>
          </MuiLink>
        );
      },
      isSortVisible: true,
      className: "cell-contact-name",
    },
    {
      id: ClientContactResultFormKey.usedByClientsName,
      label: getLabelFromContacts(createClientSiteCoreKeys.usedByClient),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: any) => (
        <MuiBox className="used-by-client-wrapper">
          <MuiBox className="used-by-client-text">
            {isNotEmpty(row.usedByClients) && (
              <MuiTypography variant="h3">
                <MuiEllipseLabel
                  text={`${row.usedByClients[0].clientName}, ${row.usedByClients[0].addressCountry}`}
                ></MuiEllipseLabel>
              </MuiTypography>
            )}
          </MuiBox>
          {isNotEmpty(row.usedByClients) && row.usedByClients.length > 1 && (
            <MuiCustomMenu data={row.usedByClients} />
          )}
        </MuiBox>
      ),
      className: "cell-used-by-client",
    },
    {
      id: ClientContactResultFormKey.contactType,
      isEmptyValue: true,
      label: fetchLabel(
        createClientSiteCoreKeys.contactsTitle,
        createClientSiteCoreKeys.contactType,
      ),
      render: (row: any) => {
        const selectedContactType: ChipOptionsDataType[] = row.contactType.map(
          (type: string) => ({
            label: fetchValueByKeyOfDropdown(
              createClientSiteCoreKeys.addContactTitle,
              createClientSiteCoreKeys.contactTypeDropdown,
              type,
            ),
            value: fetchValueByKeyOfDropdown(
              createClientSiteCoreKeys.addContactTitle,
              createClientSiteCoreKeys.contactTypeDropdown,
              type,
            ),
          }),
        );

        return (
          <MuiBox>
            <MuiChipWithPopover
              data={selectedContactType}
              handleDelete={() => {}}
              id={"selected-contact-types"}
              isEditable={false}
              headerLabel={fetchLabel(
                createClientSiteCoreKeys.contactsTitle,
                createClientSiteCoreKeys.contactType,
              )}
              gridRef={React.createRef<HTMLElement>()}
              countToWrap={1}
              showMoreARIALabel={commonARIALabel.showMoreDetails}
              removeChipNameARIALabel={commonARIALabel.removeChipName}
              closePopoverARIALabel={commonARIALabel.closePopover}
            />
            <MuiTypography variant="h3"></MuiTypography>
          </MuiBox>
        );
      },
      isSortVisible: true,
    },
    {
      id: ClientContactResultFormKey.isSurveyContact,
      label: getLabelFromContacts(createClientSiteCoreKeys.surveyContact),
      isSortVisible: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {getYesNoString(row.isSurveyContact)}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: ClientContactResultFormKey.status,
      label: getLabelFromContacts(createClientSiteCoreKeys.status),
      render: (row: any) => {
        const activeValue = fetchValueByKeyOfDropdown(
          createClientSiteCoreKeys.contactsTitle,
          createClientSiteCoreKeys.contactStatusList,
          row.status,
        );
        return <StatusBadge status={row.status} label={activeValue} />;
      },
      isSortVisible: true,
      className: "cell-status",
    },
    {
      id: ClientContactResultFormKey.contactNumber,
      label: getLabelFromContacts(createClientSiteCoreKeys.contactNo),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-contact",
    },
    {
      id: ClientContactResultFormKey.lastModifiedOn,
      label: getLabelFromContacts(createClientSiteCoreKeys.modifiedOn),
      isSortVisible: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {dayjs.utc(row.lastModifiedOn).format(userCountryDateFormat)}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: "action",
      label: getLabelFromContacts(createClientSiteCoreKeys.action),
      className: "cell-actions",
      render: (row: any) =>
        isEditable ? (
          <MuiBox>
            <OptionMenu
              clientData={row}
              onClickOptionEdit={onClientEdit}
              onClickOptionDelete={deleteContactAction}
            />
          </MuiBox>
        ) : (
          <></>
        ),
    },
  ];

  const renderSearchIcon = () => (
    <InputAdornment position="start">
      <SearchIcon />
    </InputAdornment>
  );

  const tableHeaderForm = [
    <FormController
      key={ClientContactResultFormKey.contactName}
      control={control}
      name={ClientContactResultFormKey.contactName}
    >
      <MuiTextField
        id={`${pageId}-${ClientContactResultFormKey.contactName}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        showCloseIcon={true}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ClientContactResultFormKey.usedByClientsName}
      key={ClientContactResultFormKey.usedByClientsName}
    >
      <MuiTextField
        id={`${pageId}-${ClientContactResultFormKey.usedByClientsName}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        showCloseIcon={true}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ClientContactResultFormKey.contactType}
      key={ClientContactResultFormKey.contactType}
    >
      <MuiSelect
        visibleDefaultSelectOption
        label=""
        options={fetchDropdownValues(
          createClientSiteCoreKeys.addContactTitle,
          createClientSiteCoreKeys.contactTypeDropdown,
        )}
        value=""
        id={`${pageId}-${ClientContactResultFormKey.contactType}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ClientContactResultFormKey.isSurveyContact}
      key={ClientContactResultFormKey.isSurveyContact}
    >
      <MuiSelect
        visibleDefaultSelectOption
        label=""
        options={fetchDropdownValues(
          createClientSiteCoreKeys.contactsTitle,
          createClientSiteCoreKeys.surveyContactList,
          true,
        )}
        value=""
        id={`${pageId}-${ClientContactResultFormKey.isSurveyContact}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ClientContactResultFormKey.status}
      key={ClientContactResultFormKey.status}
    >
      <MuiSelect
        visibleDefaultSelectOption
        label=""
        options={fetchDropdownValues(
          createClientSiteCoreKeys.contactsTitle,
          createClientSiteCoreKeys.contactStatusList,
          true,
        )}
        value=""
        id={`${pageId}-${ClientContactResultFormKey.status}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ClientContactResultFormKey.contactNumber}
      key={ClientContactResultFormKey.contactNumber}
    >
      <MuiTextField
        id={`${pageId}-${ClientContactResultFormKey.contactNumber}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        showCloseIcon={true}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ClientContactResultFormKey.lastModifiedOn}
      key={ClientContactResultFormKey.lastModifiedOn}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          format={userCountryDateFormat}
          onChange={handleMuiDateChange}
          clearable
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
    <></>,
  ];

  const clientData = useSelector(createClientSelector);

  return (
    <ClientContactResultWrapper>
      <MuiGrid container>
        <MuiGrid size={{ xs: 12 }} className="header-right">
          <MuiTable
            ref={tableRef}
            pageId={pageId}
            tableHeaderForm={
              isRetailClient(clientData.clientTypeId)
                ? tableHeaderForm.filter((item, index: number) => index !== 1) // remove used by client column
                : tableHeaderForm
            }
            columns={
              isRetailClient(clientData.clientTypeId)
                ? columns.filter((item, index: number) => index !== 1) // remove used by client column
                : columns
            }
            isLastColumnSticky={isEditable}
            tableResults={[...filteredParentContactResults]}
            priorityResults={[...filteredCurrentClientContactResults]}
            noDataInApi={rows.length === 0}
            noResultMessage={commonLabels.noRecordsFound}
            emptyMessage={getLabelFromContacts(
              createClientSiteCoreKeys.noRecordsAvailable,
            )}
          />
        </MuiGrid>
      </MuiGrid>
    </ClientContactResultWrapper>
  );
};
